import axios from 'axios'
import Vue from 'vue'
import Vuex from 'vuex'

Vue.use(Vuex)

const apiBaseURL = process.env.VUE_APP_API_URL

const state = () => ({
  ui: {
    clientVersion: process.env.VUE_APP_VERSION,
    toastMessage: '',
    projectDisplayComp: 'GProject',
    showCommentForm: false,
    currentProject: '',
    announcements: [],
    projectStatusOptions: '',
    projectSemesterOptions: '',
    error: {},
  },
  user: {
    isLoggedIn: false,
    authStatus: '',
    currentUser: '',
  },
  userList: [],
  projects: [],
  comments: [],
  contracts: [],
  devSitesToBeCreated: [],
})

const mutations = {
  displayDetails(state) {
    state.ui.projectDisplayComp = 'GProjectDetails'
  },
  hideDetails(state) {
    state.ui.projectDisplayComp = 'GProject'
  },
  setToastMessage(state, message) {
    state.ui.toastMessage = message
  },
  closeProjectDetails(state) {
    state.ui.showProjectDetails = false
  },
  setProjects(state, { projects, metadata }) {
    state.projects = projects
    state.ui.toastMessage = metadata
  },
  setProjectStatusOptions(state, options) {
    state.ui.projectStatusOptions = options
  },
  setContracts(state, contracts) {
    state.contracts = contracts
  },
  setProjectSemesterOptions(state, options) {
    state.ui.projectSemesterOptions = options
  },
  setCurrentProject(state, { project }) {
    state.ui.showCommentForm = false
    state.ui.currentProject = project.id
    state.ui.showProjectDetails = true
  },
  setCurrentProjectByID(state, id) {
    state.ui.currentProject = id
  },
  setUserList(state, users) {
    state.userList = users
  },
  setAnnouncements(state, announcements) {
    state.ui.announcements = announcements
  },
  setCurrentUser(state, userRecord) {
    state.user.currentUser = userRecord
    state.user.isLoggedIn = true
  },
  logout(state) {
    state.user.isLoggedIn = false
    state.user.currentUser = ''
  },
  toggleCommentForm(state) {
    state.ui.showCommentForm = !state.ui.showCommentForm
  },
  setComments(state, { comments, message }) {
    state.toastMessage = message
    state.comments = comments
  },
  setDevSitesToBeCreated(state, sites) {
    state.devSitesToBeCreated = sites
  },
}

const actions = {
  async cd_callAPI({ rootGetters }, config) {
    try {
      const result = await axios({
        url: `${apiBaseURL}${config.url}`,
        method: config.method || 'get',
        data: config.data,
        headers: {
          Authorization: `Bearer ${rootGetters.currentUser.token ||
            config.token}`,
        },
      })
      return result.data
    } catch (err) {
      throw new Error(err)
    }
  },
  async getDevSitesToBeCreated({ rootGetters, commit }) {
    return new Promise((resolve, reject) => {
      axios({
        url: `${apiBaseURL}/d/dev-sites`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${rootGetters.currentUser.token}`,
        },
      })
        .then(resp => {
          commit('setDevSitesToBeCreated', resp.data)
          resolve()
        })
        .catch(err => {
          console.error(err)
          reject(err)
        })
    })
  },
  logout({ commit }) {
    commit('logout')
    window.localStorage.removeItem('user')
    window.location.reload()
  },
  email({ rootGetters }, emailDetails) {
    return new Promise((resolve, reject) => {
      axios({
        url: `${apiBaseURL}/d/admin/email`,
        method: 'POST',
        headers: {
          Authorization: `Bearer ${rootGetters.currentUser.token}`,
        },
        data: {
          emailDetails,
        },
      })
        .then(resp => {
          resolve(resp)
        })
        .catch(err => {
          console.error(err)
          reject(err)
        })
    })
  },
  async setup({ commit }) {
    try {
      const response = await axios({ url: `${apiBaseURL}/d`, method: 'GET' })
      const announcements = response.data.data
      commit('setAnnouncements', announcements)
      return response.data.metadata.message
    } catch (err) {
      throw new Error(err)
    }
  },
  async createDevSite({ dispatch, rootGetters }, devSite) {
    try {
      const response = await axios({
        url: `${apiBaseURL}/d/dev-sites`,
        method: 'POST',
        headers: {
          Authorization: `Bearer ${rootGetters.currentUser.token}`,
        },
        data: devSite,
      })
      dispatch('getDevSitesToBeCreated')
      return response
    } catch (err) {
      throw Error(err)
    }
  },
  async updateProjectLead({ dispatch, rootGetters }, projectDetails) {
    const result = await axios({
      url: `${apiBaseURL}/d/projects/lead`,
      method: 'POST',
      headers: {
        Authorization: `Bearer ${rootGetters.currentUser.token}`,
      },
      data: {
        ofdit_lead: projectDetails.ofdit_lead,
        project_id: projectDetails.project_id,
      },
    })
    dispatch('getProjects')
    return result
  },
  async updateProjectStatus({ dispatch, rootGetters }, projectDetails) {
    const result = await axios({
      url: `${apiBaseURL}/d/projects/status`,
      method: 'POST',
      headers: {
        Authorization: `Bearer ${rootGetters.currentUser.token}`,
      },
      data: projectDetails,
    })
    dispatch('getProjects')
    return result
  },
  updateProjectDeliveryDate({ rootGetters }, projectDetails) {
    return new Promise((resolve, reject) => {
      axios({
        url: `${apiBaseURL}/d/projects/delivery_date`,
        method: 'POST',
        headers: {
          Authorization: `Bearer ${rootGetters.currentUser.token}`,
        },
        data: {
          delivery_date: projectDetails.delivery_date,
          project_id: projectDetails.project_id,
        },
      })
        .then(resp => {
          resolve(resp)
        })
        .catch(err => {
          console.error(err)
          reject(err)
        })
    })
  },
  async updateProjectTargetLaunchSemester({ rootGetters }, projectDetails) {
    return new Promise((resolve, reject) => {
      axios({
        url: `${apiBaseURL}/d/projects/target_launch_semester`,
        method: 'POST',
        headers: {
          Authorization: `Bearer ${rootGetters.currentUser.token}`,
        },
        data: {
          semester: projectDetails.target_launch_semester,
          project_id: projectDetails.project_id,
        },
      })
        .then(resp => {
          resolve(resp)
        })
        .catch(err => {
          console.error(err)
          reject(err)
        })
    })
  },
  getContracts({ commit, rootGetters }) {
    return new Promise((resolve, reject) => {
      axios({
        url: `${apiBaseURL}/d/contracts`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${rootGetters.currentUser.token}`,
        },
      })
        .then(resp => {
          const contracts = resp.data.data
          commit('setContracts', contracts)
          resolve(resp)
        })
        .catch(err => {
          console.error(err)
          reject(err)
        })
    })
  },
  getContractsByProgram({ commit, rootGetters }, { program }) {
    const programID = parseInt(program)
    return new Promise((resolve, reject) => {
      axios({
        url: `${apiBaseURL}/d/contracts/program/${programID}`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${rootGetters.currentUser.token}`,
        },
      })
        .then(resp => {
          const contracts = resp.data.data
          commit('setContracts', contracts)
          resolve(resp)
        })
        .catch(err => {
          console.error(err)
          reject(err)
        })
    })
  },
  getProjects({ rootGetters, commit }) {
    return new Promise((resolve, reject) => {
      axios({
        url: `${apiBaseURL}/d/projects`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${rootGetters.currentUser.token}`,
        },
      })
        .then(resp => {
          const projects = resp.data.data
          const metadata = resp.data.metadata
          commit('setProjects', { projects, metadata })
          resolve(resp)
        })
        .catch(err => {
          console.error(err)
          reject(err)
        })
    })
  },
  getProjectsByProgram({ commit, rootGetters }, { program }) {
    const programID = parseInt(program)
    return new Promise((resolve, reject) => {
      axios({
        url: `${apiBaseURL}/d/projects/program/${programID}`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${rootGetters.currentUser.token}`,
        },
      })
        .then(resp => {
          const projects = resp.data.data
          const metadata = resp.data.metadata
          commit('setProjects', { projects, metadata })
          resolve(resp)
        })
        .catch(err => {
          console.error(err)
          reject(err)
        })
    })
  },
  getComments({ commit, rootGetters }) {
    return new Promise((resolve, reject) => {
      axios({
        url: `${apiBaseURL}/d/comments`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${rootGetters.currentUser.token}`,
        },
      })
        .then(resp => {
          const comments = resp.data.data
          const metadata = resp.data.metadata
          commit('setComments', { comments, metadata })
          resolve(resp)
        })
        .catch(err => {
          console.error(err)
          reject(err)
        })
    })
  },
  getCommentsByProgram({ commit, rootGetters }, { program }) {
    const programID = parseInt(program)
    return new Promise((resolve, reject) => {
      axios({
        url: `${apiBaseURL}/d/comments/program/${programID}`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${rootGetters.currentUser.token}`,
        },
      })
        .then(resp => {
          const comments = resp.data.data
          const metadata = resp.data.metadata
          commit('setComments', { comments, metadata })
          resolve(resp)
        })
        .catch(err => {
          console.error(err)
          reject(err)
        })
    })
  },
  createComment({ commit, dispatch, rootGetters }, comment) {
    return new Promise((resolve, reject) => {
      axios({
        url: `${apiBaseURL}/d/comments`,
        method: 'POST',
        headers: {
          Authorization: `Bearer ${rootGetters.currentUser.token}`,
        },
        data: {
          project_id: comment.project_id,
          author_id: comment.author_id,
          content: comment.content,
          created_date: comment.created_date,
        },
      })
        .then(resp => {
          if (!('customSuccessMessage' in comment)) {
            const message = 'Your comment has been recorded.'
            commit('setToastMessage', message)
          }
          dispatch('getComments')
          resolve(resp)
        })
        .catch(err => {
          console.error(err)
          reject(err)
        })
    })
  },
  closeProjectDetails({ commit }) {
    commit('closeProjectDetails')
  },
  toggleCommentForm({ commit }) {
    commit('toggleCommentForm')
  },
  getUserList({ commit }) {
    axios({ url: `${apiBaseURL}/d/users`, method: 'GET' })
      .then(resp => {
        commit('setUserList', resp.data.data)
      })
      .catch(err => {
        console.error(err)
      })
  },
  login({ commit }, credentials) {
    return new Promise((resolve, reject) => {
      axios({
        url: `${apiBaseURL}/login`,
        method: 'POST',
        data: {
          username: credentials.username,
          password: credentials.password,
        },
      })
        .then(resp => {
          commit('setCurrentUser', resp.data)
          const userAsString = JSON.stringify(resp.data)
          window.localStorage.setItem('user', userAsString)
          resolve(resp)
        })
        .catch(err => {
          console.error(err)
          reject(err)
        })
    })
  },
  getOptions({ commit, rootGetters }) {
    return new Promise((resolve, reject) => {
      axios({
        url: `${apiBaseURL}/d/admin/status`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${rootGetters.currentUser.token}`,
        },
      })
        .then(resp => {
          commit('setProjectStatusOptions', resp.data.data)
          resolve(resp)
        })
        .catch(err => {
          console.error(err)
          reject(err)
        })
    })
  },
  getSemesters({ commit, rootGetters }) {
    return new Promise((resolve, reject) => {
      axios({
        url: `${apiBaseURL}/d/admin/semesters`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${rootGetters.currentUser.token}`,
        },
      })
        .then(resp => {
          commit('setProjectSemesterOptions', resp.data.data)
          resolve(resp)
        })
        .catch(err => {
          console.error(err)
          reject(err)
        })
    })
  },
  setCurrentUser({ commit }, userRecord) {
    commit('setCurrentUser', userRecord)
  },
  setCurrentProject({ commit }, { project }) {
    commit('setCurrentProject', { project })
  },
  setCurrentProjectByID({ commit }, { id }) {
    return new Promise(resolve => {
      commit('setCurrentProjectByID', id)
      resolve()
    })
  },
}

const getters = {
  announcements: state => state.ui.announcements,
  isLoggedIn: state => !!state.user.isLoggedIn,
  authStatus: state => state.user.authStatus,
  currentUser: state => state.user.currentUser,
  showProjectDetails: state => state.ui.showProjectDetails,
  showCommentForm: state => state.ui.showCommentForm,
  activeTimesheets: state =>
    state.timesheets.filter(
      timesheet =>
        timesheet.Status === 'Sent' || timesheet.Status === 'Appointed',
    ),
  numberOfDevSitesToBeCreated: state => state.devSitesToBeCreated.length,
  dataImportedProjects: state =>
    state.projects.filter(
      project => project.project_status === 'Data imported',
    ),
  currentProject: state =>
    state.projects.filter(project => project.id === state.ui.currentProject)[0],
  developerInformation: state => {
    const id = state.ui.currentProject
    if (typeof id === 'undefined') {
      return null
    } else {
      return state.contracts.filter(contract => contract.project_id === id)
    }
  },
  userList: state => state.userList.filter(user => user.is_grizzly_user),
  activeProjects: state =>
    state.projects.filter(
      project =>
        !(project.stage === 'Approved' || project.stage === 'Cancelled'),
    ),
  allProjects: state => state.projects,
  allMyProjects: state =>
    state.projects.filter(
      project => project.ofdit_lead_id === state.user.currentUser.id,
    ),
  allMyActiveProjects: state =>
    state.projects.filter(
      project =>
        project.ofdit_lead_id === state.user.currentUser.id &&
        !(project.stage === 'Approved' || project.stage === 'Cancelled'),
    ),
  projectsToBeAssigned: state =>
    state.projects.filter(
      project => project.project_status === 'Contract executed',
    ),
  ofditStaff: state => state.userList.filter(user => user.program_id === 14),
  projectComments: state => {
    const id = state.ui.currentProject
    if (typeof id === 'undefined') {
      return null
    } else {
      return state.comments.filter(comment => comment.project_id === id)
    }
  },
  isOfdit: state => {
    if (state.user.currentUser.program === 'OFDIT') {
      return true
    } else {
      return false
    }
  },
  isAcademicStaff: state => {
    if (
      state.user.currentUser.program === 'OFDIT' ||
      state.user.currentUser.program === 'ADMIN'
    ) {
      return false
    } else {
      return true
    }
  },
}

export default {
  state,
  getters,
  actions,
  mutations,
}
